import { Box, palette, Stack, TagChip, Typography } from '@munifocus/ui';
import type { AccessibleScope } from '@munifocus/contracts-gov';
import { useTranslation } from 'react-i18next';

/**
 * The roles the account holds, each next to the place it is held.
 *
 * Settings used to read `user.roles` — the legacy role column on the account
 * itself, which only ever carried `officer`/`supervisor`. Authority now lives
 * in seats (`government_membership`), appointments and company memberships,
 * so a clerk saw "—" while holding a perfectly real clerk seat. The session's
 * scopes are where those are folded together; this reads them, and falls back
 * to the account roles only for an account that holds no scope at all.
 *
 * The place travels with the role, as it does in the account menu: a person can
 * be the clerk at one municipality and an officer at another.
 */
export function ProfileRoles({
  scopes,
  accountRoles = [],
}: {
  scopes: AccessibleScope[];
  accountRoles?: string[];
}) {
  const { t } = useTranslation();
  const held = scopes.filter((scope) => scope.roles.length > 0);

  if (held.length === 0) {
    if (accountRoles.length === 0) return <>—</>;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {accountRoles.map((role) => (
          <TagChip key={role} label={t(`gov.settings.role.${role}`, { defaultValue: role })} />
        ))}
      </Box>
    );
  }

  return (
    <Stack spacing={0.75}>
      {held.map((scope) => (
        <Stack
          key={`${scope.kind}:${scope.id}`}
          direction="row"
          spacing={0.5}
          useFlexGap
          sx={{ flexWrap: 'wrap', alignItems: 'center' }}
        >
          {scope.roles.map((role) => (
            <TagChip key={role} label={t(`gov.admin.roles.${role}`, { defaultValue: role })} />
          ))}
          {scope.name && (
            <Typography sx={{ fontSize: 13, color: palette.text.muted }}>
              {t('gov.nav.atPlace', { place: scope.name })}
            </Typography>
          )}
        </Stack>
      ))}
    </Stack>
  );
}
